import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {CheckGlyph} from '@/components/ui/icons'
import {useRegisterArlReport, type SgsstAssessment} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {formatDate, formatDateOnly, formatRatePct} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

type ArlDeadlineEstado = 'vigente' | 'proximo' | 'vencido'
// `arl_dias_restantes` lo calcula el backend con `bogota_today()`, nunca `new Date()` del navegador.
type ArlAssessment = SgsstAssessment & {
  arl_deadline_estado?: ArlDeadlineEstado | null
  arl_dias_restantes?: number | null
}

export function ArlBanner({
  businessId,
  assessment,
  editable,
}: {
  businessId: string
  assessment: ArlAssessment
  editable: boolean
}) {
  const {t} = useTranslation()
  const register = useRegisterArlReport(businessId)
  const [error, setError] = useState<string | null>(null)
  const band = assessment.snapshot_band
  if (!assessment.autoevaluacion_completed_at || !band || band === 'aceptable') return null

  // Un band no-aceptable siempre trae `arl_deadline` (art. 28); `vigente` es el valor a prueba de fallos.
  const estado: ArlDeadlineEstado = assessment.arl_deadline_estado ?? 'vigente'

  return (
    <div className="surface space-y-2.5 px-4 py-3.5" data-testid="arl-banner">
      {/* U18(a): check y no candado; el candado de la casa dice «falta un paso». */}
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <CheckGlyph />
        {t('sgsst.arl.registeredLine', {
          year: assessment.period_year,
          date: formatDate(assessment.autoevaluacion_completed_at),
          pct: formatRatePct(assessment.snapshot_pct ?? 0),
          band: t(`sgsst.bands.${band}.label`),
        })}
      </p>
      <div className={cn('space-y-1 rounded-lg px-3 py-2 text-sm', band === 'critico' ? 'tone-red' : 'tone-amber')}>
        <p className="font-medium">{t(band === 'critico' ? 'sgsst.arl.planCritico' : 'sgsst.arl.planModerado')}</p>
        {assessment.arl_deadline && estado === 'vigente' && (
          <p>{t('sgsst.arl.deadline', {date: formatDateOnly(assessment.arl_deadline)})}</p>
        )}
      </div>
      {assessment.arl_deadline && estado !== 'vigente' && (
        <p
          className={cn('rounded-lg px-3 py-2 text-sm font-medium', estado === 'vencido' ? 'tone-red' : 'tone-amber')}
          data-testid={`arl-banner-estado-${estado}`}>
          {assessment.arl_dias_restantes != null
            ? t(estado === 'vencido' ? 'sgsst.arl.deadlineVencidoConDias' : 'sgsst.arl.deadlineProximoConDias', {
                count: Math.abs(assessment.arl_dias_restantes),
                date: formatDateOnly(assessment.arl_deadline),
              })
            : t(estado === 'vencido' ? 'sgsst.arl.deadlineVencido' : 'sgsst.arl.deadlineProximo', {
                date: formatDateOnly(assessment.arl_deadline),
              })}
        </p>
      )}
      {assessment.arl_reported_at ? (
        <p
          className="flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm text-accent-foreground"
          data-testid="arl-sent">
          <CheckGlyph />
          {t('sgsst.arl.sent', {date: formatDate(assessment.arl_reported_at)})}
        </p>
      ) : (
        editable && (
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant={estado === 'vigente' ? 'outline' : 'default'}
              disabled={register.isPending}
              onClick={() => register.mutate(undefined, {onError: (err) => setError(translateApiError(err))})}>
              {t('sgsst.arl.register')}
            </Button>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        )
      )}
      <p className="text-xs text-muted-foreground">{t('sgsst.arl.fondoHint')}</p>
    </div>
  )
}
